import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, MessageCircle } from "lucide-react";
import { MENU } from "../mock";
import { useI18n } from "../lib/i18n";
import { useSettings } from "../lib/settings";
import { buildWhatsAppMessageUrl } from "../lib/whatsapp";
import { fireGoogleAdsConversion } from "../lib/googleAds";
import { T } from "../lib/autoTranslate";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [toursOpen, setToursOpen] = useState(false);
  const { lang, setLang, LANGS, t, loading } = useI18n();
  const { settings } = useSettings();
  const s = settings || {};
  const location = useLocation();

  const currentLang = LANGS.find((l) => l.code === lang) || LANGS[0];

  const tourItems = MENU.filter((m) => m.href.startsWith("/tours"));
  const otherItems = MENU.filter((m) => !m.href.startsWith("/tours"));

  return (
    <header className="sticky top-0 z-40 bg-[var(--ri-cream)]/90 backdrop-blur border-b border-[var(--ri-border)]">
      <div className="container-x flex items-center justify-between h-[68px]">
        <Link to="/" className="flex items-center gap-2" onClick={() => setOpen(false)}>
          <span className="brand-script text-[26px] leading-none text-[var(--ri-yellow-dark)]">Istanbul</span>
          <span className="font-serif-display text-[20px] leading-none text-[var(--ri-red)]">Free Walking</span>
          <span className="brand-script text-[22px] leading-none text-[var(--ri-yellow-dark)]">Tours</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-[14.5px] text-[var(--ri-ink-soft)]">
          <div
            className="relative"
            onMouseEnter={() => setToursOpen(true)}
            onMouseLeave={() => setToursOpen(false)}
          >
            <button className="flex items-center gap-1 hover:text-[var(--ri-ink)] link-underline">
              Tours <ChevronDown size={14} />
            </button>
            {toursOpen && (
              <div className="absolute top-full left-0 pt-3">
                <div className="bg-white rounded-xl shadow-xl border border-[var(--ri-border)] py-2 min-w-[220px]">
                  {tourItems.map((m) => (
                    <Link
                      key={m.key}
                      to={m.href}
                      className={`block px-4 py-2 text-[14px] hover:bg-[var(--ri-cream)] ${
                        location.pathname === m.href ? "text-[var(--ri-red)] font-semibold" : "text-[var(--ri-ink)]"
                      }`}
                    >
                      <T>{m.label}</T>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {otherItems.map((m) => (
            <Link
              key={m.key}
              to={m.href}
              className={`link-underline hover:text-[var(--ri-ink)] ${
                location.pathname === m.href ? "text-[var(--ri-ink)] font-semibold" : ""
              }`}
            >
              <T>{m.label}</T>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 min-h-[44px] rounded-full text-[13px] hover:bg-black/5 transition-colors"
              aria-label="Change language"
            >
              <Globe size={15} />
              <span className="hidden sm:inline">{currentLang.flag} {currentLang.code.toUpperCase()}</span>
              <ChevronDown size={13} />
            </button>
            {langOpen && (
              <div
                className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-[var(--ri-border)] py-2 min-w-[160px] z-50"
                onMouseLeave={() => setLangOpen(false)}
              >
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => {
                      setLang(l.code);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-[14px] hover:bg-[var(--ri-cream)] flex items-center gap-2 ${
                      lang === l.code ? "text-[var(--ri-red)] font-semibold" : ""
                    }`}
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link to="/tours/small-group" className="btn-primary hidden sm:inline-flex text-[13.5px]">
            {t("book_a_tour")}
          </Link>

          {s.show_whatsapp_hero_button !== false && s.whatsapp_number && (
            <a
              href={buildWhatsAppMessageUrl(s.whatsapp_number, s.whatsapp_hero_message)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireGoogleAdsConversion(s.google_ads_whatsapp_send_to)}
              className="btn-whatsapp inline-flex items-center gap-1.5 text-[13.5px] min-h-[44px] !py-0 !px-3.5"
              aria-label="Chat on WhatsApp"
            >
              <MessageCircle size={16} />
              <span className="hidden md:inline">WhatsApp</span>
            </a>
          )}

          <button className="lg:hidden text-[var(--ri-ink)] p-2.5 -mr-1" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {loading && (
        <div className="h-0.5 bg-[var(--ri-red)] animate-pulse" />
      )}

      {open && (
        <div className="lg:hidden border-t border-[var(--ri-border)] bg-[var(--ri-cream)]">
          <div className="container-x py-4 flex flex-col gap-3 text-[15px]">
            {MENU.map((m) => (
              <Link key={m.key} to={m.href} onClick={() => setOpen(false)} className="py-1">
                <T>{m.label}</T>
              </Link>
            ))}
            {s.show_whatsapp_hero_button !== false && s.whatsapp_number && (
              <a
                href={buildWhatsAppMessageUrl(s.whatsapp_number, s.whatsapp_hero_message)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  fireGoogleAdsConversion(s.google_ads_whatsapp_send_to);
                  setOpen(false);
                }}
                className="btn-whatsapp inline-flex items-center justify-center gap-2 mt-1"
              >
                <MessageCircle size={17} /> Chat on WhatsApp
              </a>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
